<script lang="ts">
  import { Icon } from '@shared/icons';
</script>

<main>
  <!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- TODO(subpath): wrap with resolve() -->
  <a href="/" class="back-link">← Home</a>

  <h1>Your packing companion</h1>

  <section class="intro">
    <p class="lead">
      StuffPack is your <strong>gear inventory and pack planner</strong> today, growing into a complete packing companion.
    </p>
  </section>

  <section class="lifecycle">
    <div class="lifecycle-step active">
      <span class="step-number">1</span>
      <h3>What do I own?</h3>
    </div>
    <span class="lifecycle-arrow">→</span>
    <div class="lifecycle-step">
      <span class="step-number">2</span>
      <h3>What should I bring?</h3>
    </div>
    <span class="lifecycle-arrow">→</span>
    <div class="lifecycle-step">
      <span class="step-number">3</span>
      <h3>Am I ready?</h3>
    </div>
    <span class="lifecycle-arrow">→</span>
    <div class="lifecycle-step">
      <span class="step-number">4</span>
      <h3>How did it go?</h3>
    </div>
    <span class="lifecycle-arrow">→</span>
    <div class="lifecycle-step">
      <span class="step-number">5</span>
      <h3>How can I pack better?</h3>
    </div>
  </section>

  <section class="today-section">
    <h2 class="section-heading">What you can do today</h2>
    <div class="features">
      <div class="feature-card">
        <h2><Icon icon="gear" />Gear Inventory</h2>
        <p>One place for everything you own. Update a weight once — it updates everywhere.</p>
        <ul>
          <li>Organize gear into custom categories</li>
          <li>Track weight, quantity, and price</li>
          <li>Photos and links on every item</li>
          <li>Markdown descriptions on Gear and Packs</li>
          <li>Flag items as worn, consumable, or starred</li>
          <li>Search, filter, and sort your collection</li>
        </ul>
      </div>

      <div class="feature-card">
        <h2><Icon icon="packs" />Packs</h2>
        <p>Build reusable loadouts you can rebuild for every trip — your gear stays a single source of truth.</p>
        <ul>
          <li>Create packs as reusable templates</li>
          <li>See total weight with a donut chart</li>
          <li>Break down weight by category, item count, or price</li>
          <li>Gear is associated, not copied — update once, every pack reflects it</li>
        </ul>
      </div>

      <div class="feature-card">
        <h2><Icon icon="share" />Sharing & Import</h2>
        <p>Bring your existing gear with you, then share what you've built with friends.</p>
        <ul>
          <li>Import from CSV or other gear tools</li>
          <li>Categories, weights, descriptions — everything transfers</li>
          <li>Share any pack via link</li>
          <li>Sync across all your devices</li>
        </ul>
      </div>
    </div>
  </section>

  <section class="coming-section">
    <h2 class="section-heading">Coming with Plus</h2>
    <div class="features">
      <div class="feature-card highlight">
        <div class="card-header">
          <h2><Icon icon="check-circle" />Packing Checklist</h2>
          <span class="tier-tag">Coming with Plus</span>
        </div>
        <p>Track your packing progress in real time. Know exactly what's packed, what's left, and when you're ready.</p>
        <ul>
          <li>Check off each item as it goes in your bag</li>
          <li>See your packing progress at a glance</li>
          <li>Get a "100% packed" confirmation</li>
          <li>Pre-trip warnings for uncharged, unpurchased, or unprepped items</li>
        </ul>
      </div>

      <div class="feature-card">
        <div class="card-header">
          <h2><Icon icon="trips" />Post-Trip Follow-Up</h2>
          <span class="tier-tag">Coming with Plus</span>
        </div>
        <p>Get home and know exactly what needs to happen. No more forgetting to clean your filter.</p>
        <ul>
          <li>Flag items for post-trip follow-up</li>
          <li>Reminders like "backflush filter" or "deactivate Garmin"</li>
          <li>Review what worked and what you'd change</li>
          <li>Track your trips over time</li>
        </ul>
      </div>
    </div>
  </section>

  <section class="comparison">
    <h2>Already tracking your gear somewhere else?</h2>
    <p>Bring it all with you. Import your existing gear data and pick up right where you left off.</p>
    <ul class="migration-list">
      <li>Free forever — up to 500 gear items</li>
      <li>Photos and links on items; markdown on Gear and Packs; sharing included</li>
      <li>One source of truth — change a weight once and every pack updates</li>
    </ul>
  </section>

  <section class="cta">
    <p>Ready to get organized?</p>
    <!-- eslint-disable-next-line svelte/no-navigation-without-resolve -- TODO(subpath): wrap with resolve() -->
    <a href="/register" role="button">Get Started Free</a>
  </section>
</main>

<style lang="scss">
  main {
    max-width: 1200px;
    margin: 0 auto;
    padding: px(32);
  }

  .back-link {
    display: inline-block;
    margin-bottom: px(24);
    font-size: px(14.4);
    color: var(--pico-secondary);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  h1 {
    font-size: px(32);
    margin-bottom: px(32);
    text-align: center;

    @media (min-width: $bp-container-md) {
      font-size: px(40);
    }
  }

  .intro {
    text-align: center;
    margin-bottom: px(48);
  }

  .lead {
    font-size: px(17.6);
    color: var(--pico-muted-color);

    @media (min-width: $bp-container-md) {
      font-size: px(20);
    }
    max-width: 700px;
    margin: 0 auto;
  }

  .lifecycle {
    display: flex;
    flex-flow: column wrap;
    align-items: center;
    justify-content: center;
    gap: px(12);
    margin-bottom: px(56);

    @media (min-width: $bp-container-md) {
      flex-direction: row;
      gap: px(8);
    }
  }

  .lifecycle-step {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    gap: px(12);
    padding: px(12) px(16);
    border-radius: px(8);
    background: var(--pico-card-background-color);
    border: 1px solid var(--pico-card-border-color);
    min-width: px(120);

    &.active {
      border-color: var(--color-sea-green);
      background: rgba(46, 139, 87, 0.08);
    }

    h3 {
      margin: 0;
      font-size: px(12.8);
      font-weight: 600;
      text-align: left;
    }

    @media (min-width: $bp-container-md) {
      flex-direction: column;
      align-items: center;
      width: auto;
      gap: px(6);

      h3 {
        text-align: center;
      }
    }
  }

  .step-number {
    display: flex;
    align-items: center;
    justify-content: center;
    width: px(28);
    height: px(28);
    border-radius: 50%;
    font-size: px(13.6);
    font-weight: 700;

    .active & {
      background-color: var(--color-sea-green);
      color: #fff;
    }
  }

  .lifecycle-arrow {
    display: none;
    color: var(--pico-muted-color);
    font-size: px(19.2);

    @media (min-width: $bp-container-md) {
      display: block;
    }
  }

  .today-section,
  .coming-section {
    margin-bottom: px(56);
  }

  .section-heading {
    font-size: px(24);
    margin-bottom: px(24);
    text-align: center;

    @media (min-width: $bp-container-md) {
      font-size: px(28);
    }
  }

  .features {
    display: grid;
    grid-template-columns: 1fr;

    @media (min-width: $bp-container-md) {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    }
    gap: px(32);
  }

  .feature-card {
    background: var(--pico-card-background-color);
    border: 1px solid var(--pico-card-border-color);
    border-radius: var(--pico-border-radius);
    padding: px(32);

    &.highlight {
      border-color: var(--color-sea-green);
      box-shadow: 0 0 0 1px var(--color-sea-green);
    }

    h2 {
      margin-top: 0;
      margin-bottom: px(16);
      font-size: px(21.6);
      display: flex;
      align-items: center;
      gap: px(10);

      :global(svg) {
        width: 1.4em;
        height: 1.4em;
        flex-shrink: 0;
        color: var(--color-sea-green);
      }
    }

    p {
      margin-bottom: px(16);
      color: var(--pico-muted-color);
    }

    ul {
      list-style: none !important;
      padding: 0;
      margin: 0;
    }

    li {
      list-style: none !important;
      padding: px(6) 0 px(6) px(20);
      position: relative;

      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: var(--color-sea-green);
        font-weight: bold;
      }
    }
  }

  .card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: px(12);
    flex-wrap: wrap;

    h2 {
      margin-bottom: px(8);
    }
  }

  .tier-tag {
    display: inline-block;
    background-color: var(--color-sea-green);
    color: #fff;
    font-size: px(11.2);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: px(4) px(10);
    border-radius: 999px;
    white-space: nowrap;
    margin-top: px(4);
  }

  .comparison {
    text-align: center;
    margin-bottom: px(56);
    padding: px(40) px(32);
    background: var(--pico-card-background-color);
    border: 1px solid var(--pico-card-border-color);
    border-radius: var(--pico-border-radius);

    h2 {
      margin-top: 0;
      margin-bottom: px(8);
      font-size: px(24);
    }

    > p {
      color: var(--pico-muted-color);
      margin-bottom: px(24);
    }
  }

  .migration-list {
    display: inline-block;
    text-align: left;
    list-style: none !important;
    padding: 0;
    margin: 0;

    li {
      list-style: none !important;
      padding: px(4) 0 px(4) px(20);
      position: relative;

      &::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: var(--color-sea-green);
        font-weight: bold;
      }
    }
  }

  .cta {
    text-align: center;
    padding: px(48) 0;
    border-top: 1px solid var(--pico-muted-border-color);

    p {
      font-size: px(20);
      margin-bottom: px(20);
    }

    a {
      display: inline-block;
      padding: px(14) px(40);
      background-color: var(--color-sea-green);
      color: #fff;
      font-size: px(17.6);
      font-weight: 700;
      border-radius: px(8);
      text-decoration: none;

      &:hover {
        background-color: var(--color-camarone);
        color: #fff;
      }
    }
  }
</style>
